/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import type { ReactNode } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Icon } from '@/components/Icon';
import { buttonVariants } from '@/design-system';
import type { WorkspaceId } from './access.models';
import { useCurrentUser, useWorkspaceCatalogue } from './useAccess';
import { landingFor, workspacesFor } from './workspaceRoutes';

/**
 * A destination that exists but belongs to somebody else's workspace.
 *
 * <p>Names the workspace rather than refusing anonymously, because the person reading this cannot
 * grant it to themselves — the only useful thing the page can do is tell them what to ask for.
 */
function WrongWorkspace({
  required,
  landing,
}: {
  required: WorkspaceId[];
  landing: string | null;
}) {
  const { t } = useTranslation();
  const catalogue = useWorkspaceCatalogue();

  const names = required
    .map((id) => catalogue.data?.find((workspace) => workspace.id === id)?.displayName ?? id)
    .join(t('workspaceAccess.or'));

  return (
    <div className="state-center">
      <div className="state">
        <span className="state-ic" aria-hidden="true">
          <Icon name="lock" size={24} />
        </span>
        <h1 className="state-title">{t('workspaceAccess.title')}</h1>
        <p className="state-body">{t('workspaceAccess.message', { workspaces: names })}</p>
        {landing && (
          <div className="state-actions">
            <Link to={landing} className={buttonVariants()}>
              {t('workspaceAccess.back')}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Holds every routed destination to the workspace it belongs to.
 *
 * <p>Sits inside the shell rather than around it: the rail stays put, so being turned away from
 * one area still leaves the others a click away.
 *
 * <p>The application's front door is the one path nobody chose to open — it is simply where the
 * browser lands — so someone without Manufacturing is moved on to their own workspace instead of
 * being told they cannot be here. Anywhere else was asked for by name and is answered by name.
 */
export function RequireWorkspace({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const { data } = useCurrentUser();

  const held = data?.workspaces ?? [];
  const required = workspacesFor(pathname);
  if (!required || required.some((workspace) => held.includes(workspace))) {
    return <>{children}</>;
  }

  const landing = landingFor(held);
  if (pathname === '/' && landing) {
    return <Navigate to={landing} replace />;
  }
  return <WrongWorkspace required={required} landing={landing} />;
}
